import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingBag, Sparkles, Check, RotateCw, ArrowRight, Truck, Shield, Heart,
  Filter, X, ChevronLeft, ChevronRight, Tag, Search,
} from "lucide-react";
import { TornEdge } from "../../components/sport/TornEdge";
import { useCart } from "../../lib/useCart";
import { api } from "../../lib/api";
import { getProductSizes } from "../../lib/productSizes";
import { toast } from "sonner";
import {
  STORE_PRODUCTS, STORE_CATEGORIES, STORE_BRANDS, STORE_PRICE_RANGES, HERO_BANNERS,
  STORE_BRAND_LOGOS, JERSEY_SPONSOR_LOGOS,
} from "./storeProducts";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";

function priceFmt(cents) { return `${(cents / 100).toFixed(2)} €`; }

// ============================================================
// JERSEY SVG — live preview (front/back) for personalization
// ============================================================
function JerseySVG({ side = "back", colors, customName = "", customNumber = "" }) {
  const main = colors?.main || PRIMARY;
  const accent = colors?.accent || SECONDARY;
  const dark = "#1a0512";
  return (
    <svg viewBox="0 0 400 480" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={`fab-${side}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={main} stopOpacity="0.95"/>
          <stop offset="60%" stopColor={main}/>
          <stop offset="100%" stopColor={dark} stopOpacity="0.95"/>
        </linearGradient>
        <linearGradient id={`shine-${side}`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.18)"/>
          <stop offset="50%" stopColor="rgba(255,255,255,0.04)"/>
          <stop offset="100%" stopColor="rgba(0,0,0,0.35)"/>
        </linearGradient>
      </defs>
      <path d="M 80 100 L 130 70 L 170 60 Q 200 50 230 60 L 270 70 L 320 100 L 340 160 L 290 175 L 285 440 Q 285 460 265 460 L 135 460 Q 115 460 115 440 L 110 175 L 60 160 Z"
        fill={`url(#fab-${side})`} stroke={dark} strokeWidth="2"/>
      <path d="M 170 60 Q 200 80 230 60 L 230 95 Q 200 110 170 95 Z" fill={dark}/>
      <path d="M 110 175 L 115 440 L 95 440 L 85 175 Z" fill={accent} opacity="0.92"/>
      <path d="M 290 175 L 285 440 L 305 440 L 315 175 Z" fill={accent} opacity="0.92"/>
      <path d="M 60 160 L 110 175 L 105 195 L 55 180 Z" fill={accent}/>
      <path d="M 340 160 L 290 175 L 295 195 L 345 180 Z" fill={accent}/>
      <path d="M 80 100 L 130 70 L 170 60 Q 200 50 230 60 L 270 70 L 320 100 L 340 160 L 290 175 L 285 440 Q 285 460 265 460 L 135 460 Q 115 460 115 440 L 110 175 L 60 160 Z"
        fill={`url(#shine-${side})`}/>
      {side === "front" ? (
        <>
          <circle cx="200" cy="155" r="36" fill="rgba(255,255,255,0.94)" stroke={dark} strokeWidth="1.5"/>
          <text x="200" y="148" textAnchor="middle" fontSize="11" fontWeight="900" fill={main} fontFamily="Inter,sans-serif">IPH</text>
          <text x="200" y="166" textAnchor="middle" fontSize="7" fontWeight="700" fill={dark} fontFamily="Inter,sans-serif" letterSpacing="2">ISTRES</text>
          <rect x="115" y="220" width="170" height="32" fill="rgba(0,0,0,0.18)" rx="2"/>
          <text x="200" y="241" textAnchor="middle" fontSize="14" fontWeight="900" fill="white" fontFamily="Inter,sans-serif" letterSpacing="3">SPONSOR</text>
          {customName && <text x="200" y="295" textAnchor="middle" fontSize="13" fontWeight="800" fill="rgba(255,255,255,0.85)" letterSpacing="2">{(customName).slice(0,14).toUpperCase()}</text>}
          {customNumber && <text x="200" y="395" textAnchor="middle" fontSize="42" fontWeight="900" fontStyle="italic" fill={accent} stroke={dark} strokeWidth="1">{customNumber}</text>}
        </>
      ) : (
        <>
          {(customName || customNumber) ? (
            <>
              {customName && <text x="200" y="155" textAnchor="middle" fontSize="22" fontWeight="900" fill="white" letterSpacing="3">{customName.slice(0,14).toUpperCase()}</text>}
              {customNumber && <text x="200" y="335" textAnchor="middle" fontSize="160" fontWeight="900" fontStyle="italic" fill={accent} stroke={dark} strokeWidth="3">{customNumber}</text>}
            </>
          ) : (
            <>
              <text x="200" y="155" textAnchor="middle" fontSize="22" fontWeight="900" fill="rgba(255,255,255,0.45)" letterSpacing="3">VOTRE NOM</text>
              <text x="200" y="335" textAnchor="middle" fontSize="160" fontWeight="900" fontStyle="italic" fill="rgba(236,31,124,0.35)" stroke={dark} strokeWidth="3">10</text>
            </>
          )}
        </>
      )}
    </svg>
  );
}

// ============================================================
// HERO BANNER CAROUSEL
// ============================================================
function HeroCarousel({ banners }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrent(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    const id = setInterval(() => emblaApi.scrollNext(), 5500);
    return () => { clearInterval(id); emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  return (
    <section data-testid="boutique-hero-carousel" className="relative pt-24 pb-8 sm:pt-28 overflow-hidden" style={{ background: BG_DARK }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="overflow-hidden rounded-3xl shadow-[0_40px_100px_rgba(79,27,124,0.55)]" ref={emblaRef}>
          <div className="flex">
            {banners.map((b, i) => (
              <div key={b.id} className="flex-[0_0_100%] min-w-0 relative">
                <div className="relative aspect-[16/7] sm:aspect-[16/6] overflow-hidden"
                  style={{ background: `linear-gradient(120deg, ${PRIMARY} 0%, ${BG_DEEP} 60%, ${BG_DARK} 100%)` }}>
                  {/* halftone deco */}
                  <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `radial-gradient(circle, ${SECONDARY} 1px, transparent 1px)`,
                    backgroundSize: "14px 14px",
                    maskImage: "radial-gradient(ellipse at center, black, transparent 70%)",
                  }}/>
                  <img src={b.image} alt={b.title}
                    className="absolute right-0 bottom-0 h-[105%] w-auto object-contain object-right-bottom drop-shadow-2xl pointer-events-none"
                    style={{ maxWidth: "48%" }}/>
                  <div className="relative z-10 h-full flex flex-col justify-center px-5 py-7 sm:p-12 lg:p-16 max-w-[68%] sm:max-w-[60%]">
                    <div className="text-pink-300 font-black tracking-[0.3em] text-[10px] sm:text-xs uppercase mb-2">Nouveauté · 25/26</div>
                    <h2 className="text-white font-black uppercase leading-[0.95] break-words"
                      style={{ fontSize: "clamp(1.35rem, 4.4vw, 3.4rem)", letterSpacing: "-0.005em" }}>{b.title}</h2>
                    <p className="text-white/85 mt-2.5 text-[13px] sm:text-base max-w-md leading-snug">{b.subtitle}</p>
                    <a href={b.cta.to} data-testid={`hero-cta-${b.id}`}
                      className="mt-4 inline-flex items-center gap-2 self-start px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-black uppercase text-[11px] sm:text-xs tracking-widest shadow-xl hover:scale-105 transition"
                      style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
                      {b.cta.label} <ArrowRight size={14}/>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* dots */}
        <div className="flex items-center justify-center gap-2 mt-4">
          {banners.map((_, i) => (
            <button key={i} onClick={() => emblaApi?.scrollTo(i)}
              data-testid={`hero-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${current === i ? "w-8 bg-pink-400" : "w-1.5 bg-white/30 hover:bg-white/60"}`}/>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================
// BRAND AVATAR — circular logo + name pill (sidebar + product card)
// ============================================================
function BrandAvatar({ name, size = 22, withName = false, className = "" }) {
  const logo = STORE_BRAND_LOGOS[name];
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <span
        data-testid={`brand-avatar-${name}`}
        className="rounded-full bg-white shadow-sm ring-1 ring-slate-200 overflow-hidden flex items-center justify-center shrink-0"
        style={{ width: size, height: size }}
      >
        {logo ? (
          <img src={logo} alt={name} className="w-full h-full object-contain p-0.5"/>
        ) : (
          <span className="text-[8px] font-black text-slate-500">{name.slice(0, 2).toUpperCase()}</span>
        )}
      </span>
      {withName && <span className="text-[10px] font-black tracking-widest uppercase text-slate-400">{name}</span>}
    </span>
  );
}

// ============================================================
// SPONSOR BACKDROP — auto-scrolling tiled logos, slight tilt, gray-light
// Renders 2 horizontal rolling rows in opposite directions.
// ============================================================
function SponsorBackdrop() {
  // double the array for seamless infinite scroll
  const row = [...JERSEY_SPONSOR_LOGOS, ...JERSEY_SPONSOR_LOGOS, ...JERSEY_SPONSOR_LOGOS, ...JERSEY_SPONSOR_LOGOS];
  // Two rows that fill the full height, alternating direction with very low opacity.
  const rowsConfig = [
    { top: "12%",  duration: 60, reverse: false },
    { top: "38%",  duration: 75, reverse: true  },
    { top: "64%",  duration: 65, reverse: false },
    { top: "88%",  duration: 80, reverse: true  },
  ];
  return (
    <div data-testid="sponsor-backdrop" aria-hidden
      className="absolute inset-0 overflow-hidden pointer-events-none"
      style={{ transform: "rotate(-2deg) scale(1.15)" }}>
      {rowsConfig.map((cfg, rowIdx) => (
        <div
          key={rowIdx}
          className="absolute -left-1/4 -right-1/4 flex gap-16 sm:gap-24 whitespace-nowrap"
          style={{
            top: cfg.top,
            animation: `sponsor-scroll ${cfg.duration}s linear infinite ${cfg.reverse ? "reverse" : "normal"}`,
            opacity: 0.07,
          }}
        >
          {row.map((s, i) => (
            <img
              key={`${rowIdx}-${i}`}
              src={s.url}
              alt=""
              className="h-14 sm:h-20 lg:h-24 w-auto object-contain shrink-0 grayscale"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ))}
        </div>
      ))}
      <style>{`
        @keyframes sponsor-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

// ============================================================
// PRODUCT DETAIL MODAL — opens on card click
// ============================================================
function ProductDetailModal({ product, onClose, onAdd }) {
  if (!product) return null;
  const inEur = priceFmt(product.price);
  const oldEur = product.oldPrice ? priceFmt(product.oldPrice) : null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
        data-testid="product-modal-overlay"
        style={{ background: "rgba(8, 4, 18, 0.85)", backdropFilter: "blur(10px)" }}
      >
        <motion.div
          initial={{ y: 30, opacity: 0, scale: 0.97 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 30, opacity: 0, scale: 0.97 }}
          transition={{ type: "spring", damping: 26, stiffness: 240 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="product-modal"
          className="relative bg-white rounded-3xl shadow-[0_50px_120px_rgba(0,0,0,0.6)] overflow-hidden max-w-4xl w-full max-h-[90vh] flex flex-col sm:flex-row"
        >
          {/* Close */}
          <button
            onClick={onClose}
            data-testid="product-modal-close"
            className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-white/95 shadow-lg flex items-center justify-center text-slate-700 hover:bg-pink-500 hover:text-white transition"
            aria-label="Fermer"
          >
            <X size={18}/>
          </button>

          {/* Image */}
          <div className="relative w-full sm:w-1/2 bg-slate-50 flex items-center justify-center p-8 overflow-hidden">
            {product.sale && (
              <div className="absolute top-4 left-4 px-3 py-1 rounded-full font-black text-[11px] tracking-widest uppercase shadow-xl"
                style={{ background: SECONDARY, color: "white" }}>
                -{product.sale_pct}%
              </div>
            )}
            <img src={product.image} alt={product.name} className="max-w-full max-h-[400px] object-contain"/>
          </div>

          {/* Details */}
          <div className="w-full sm:w-1/2 p-6 sm:p-8 overflow-y-auto">
            <BrandAvatar name={product.brand} size={28} withName/>
            <h2 className="font-black uppercase italic mt-3 leading-tight" style={{ color: PRIMARY, fontSize: "clamp(1.4rem, 2.5vw, 2rem)" }}>
              {product.name}
            </h2>

            <div className="mt-4 flex items-baseline gap-3">
              <span className="font-black text-3xl" style={{ color: SECONDARY }}>{inEur}</span>
              {oldEur && <span className="text-base text-slate-400 line-through">{oldEur}</span>}
            </div>

            {product.soldOut && (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 text-[11px] font-black uppercase tracking-widest">
                <span className="w-2 h-2 rounded-full bg-rose-500"/> Épuisé
              </div>
            )}

            <div className="mt-5 space-y-2 text-sm text-slate-600 border-y border-slate-100 py-4">
              <div className="flex items-center gap-2">
                <Tag size={14} className="text-pink-500"/>
                <span><strong className="text-slate-900">Marque :</strong> {product.brand}</span>
              </div>
              <div className="flex items-center gap-2">
                <Truck size={14} className="text-pink-500"/>
                <span>Livraison 7-10 jours ouvrés</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield size={14} className="text-pink-500"/>
                <span>Produit officiel — soutien direct au club</span>
              </div>
            </div>

            <p className="text-slate-600 text-sm mt-5 leading-relaxed">
              Produit officiel de la boutique <strong>Istres Provence Handball</strong>.
              Ajoutez-le à votre panier pour finaliser votre commande sur le site. Chaque commande soutient directement le club et sa formation.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              {!product.soldOut ? (
                <button
                  data-testid="modal-add-to-cart"
                  onClick={() => { onAdd(product); onClose(); }}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-black uppercase text-sm tracking-widest text-white shadow-xl hover:scale-[1.02] transition"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
                >
                  <ShoppingBag size={16}/> Ajouter au panier
                </button>
              ) : (
                <button disabled className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-black uppercase text-sm tracking-widest text-white bg-slate-400 cursor-not-allowed">
                  Rupture de stock
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ============================================================
// PRODUCT CARD
// ============================================================
function ProductCard({ p, onAdd, onOpen }) {
  const inEur = priceFmt(p.price);
  const oldEur = p.oldPrice ? priceFmt(p.oldPrice) : null;
  // ⚠️ On n'utilise PAS un <button> wrapper ici car la card contient déjà
  // un <button> "Ajouter au panier" → erreur hydration React + clic mobile
  // qui buble vers le parent. On utilise un <div role="button"> à la place.
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4 }}
      data-testid={`product-${p.id}`}
      className="group relative rounded-2xl bg-white overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition border border-slate-100">
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpen?.(p)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(p); } }}
        className="block w-full text-left focus:outline-none focus:ring-2 focus:ring-pink-500 rounded-2xl cursor-pointer"
        data-testid={`product-link-${p.id}`}>
      {/* media */}
      <div className="relative aspect-square bg-slate-50 overflow-hidden">
        <img src={p.image} alt={p.name} loading="lazy"
          className="w-full h-full object-contain p-4 group-hover:scale-105 transition duration-500"/>
        {/* sale badge */}
        {p.sale && (
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full font-black text-[10px] tracking-widest uppercase shadow"
            style={{ background: SECONDARY, color: "white" }}>
            -{p.sale_pct}%
          </div>
        )}
        {/* brand avatar — top right */}
        <div className="absolute top-3 right-3">
          <BrandAvatar name={p.brand} size={28}/>
        </div>
        {p.soldOut && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="px-3 py-1.5 rounded-full bg-rose-600 text-white font-black text-[11px] tracking-widest uppercase">Épuisé</span>
          </div>
        )}
        {/* quick add — bouton CTA pleine largeur avec icône + label clair */}
        {!p.soldOut && (
          <button data-testid={`add-${p.id}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAdd(p); }}
            className="absolute bottom-3 left-3 right-3 inline-flex items-center justify-center gap-2 h-11 rounded-xl text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all font-black uppercase tracking-wider text-xs"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
            aria-label="Ajouter au panier"
            title="Ajouter au panier">
            <ShoppingBag size={16} strokeWidth={2.5}/>
            Ajouter au panier
          </button>
        )}
      </div>
      {/* meta */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <BrandAvatar name={p.brand} size={18}/>
          <span className="text-[10px] font-black tracking-widest uppercase text-slate-400">{p.brand}</span>
        </div>
        <h3 className="font-bold text-slate-900 text-sm leading-snug min-h-[40px] group-hover:text-pink-700 transition">{p.name}</h3>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-black text-lg" style={{ color: PRIMARY }}>{inEur}</span>
            {oldEur && <span className="text-xs text-slate-400 line-through">{oldEur}</span>}
          </div>
          <span className="text-pink-600 group-hover:translate-x-1 transition" aria-hidden>
            <ArrowRight size={14}/>
          </span>
        </div>
      </div>
      </div>
    </motion.div>
  );
}

// ============================================================
// FILTER SIDEBAR
// ============================================================
function FilterSidebar({ filters, setFilters, total }) {
  const toggleBrand = (b) => {
    setFilters((f) => ({ ...f, brands: f.brands.includes(b) ? f.brands.filter(x => x !== b) : [...f.brands, b] }));
  };

  return (
    <aside data-testid="boutique-sidebar" className="lg:sticky lg:top-24 space-y-6">
      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input data-testid="filter-search"
          value={filters.q}
          onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          placeholder="Rechercher un produit…"
          className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-slate-200 focus:border-pink-500 focus:outline-none text-sm shadow-sm"/>
      </div>

      {/* Categories */}
      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 text-pink-600 font-black tracking-widest text-[10px] uppercase border-b border-slate-100">Catégories</div>
        <ul className="py-2">
          {STORE_CATEGORIES.map((c) => {
            const active = filters.cat === c.id;
            return (
              <li key={c.id}>
                <button data-testid={`cat-${c.id}`}
                  onClick={() => setFilters({ ...filters, cat: c.id })}
                  className={`w-full text-left px-5 py-2.5 flex items-center justify-between text-sm transition ${active ? "bg-pink-50 text-pink-700 font-black" : "text-slate-700 hover:bg-slate-50"}`}>
                  <span>{c.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${active ? "bg-pink-500 text-white" : "bg-slate-100 text-slate-500"}`}>{c.count}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Brand */}
      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 text-pink-600 font-black tracking-widest text-[10px] uppercase border-b border-slate-100">Marques</div>
        <div className="py-3 px-4 space-y-1">
          {STORE_BRANDS.map((b) => (
            <label key={b}
              className={`flex items-center gap-3 px-2 py-2 rounded-xl cursor-pointer transition ${
                filters.brands.includes(b) ? "bg-pink-50 text-pink-700" : "text-slate-700 hover:bg-slate-50"
              }`}>
              <input type="checkbox" data-testid={`brand-${b}`}
                checked={filters.brands.includes(b)} onChange={() => toggleBrand(b)}
                className="w-4 h-4 accent-pink-500 shrink-0"/>
              <BrandAvatar name={b} size={28}/>
              <span className="text-sm font-bold flex-1">{b}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Price */}
      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-5 py-3 text-pink-600 font-black tracking-widest text-[10px] uppercase border-b border-slate-100">Prix</div>
        <div className="py-3 px-5 space-y-2">
          {STORE_PRICE_RANGES.map((r) => (
            <label key={r.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-pink-700">
              <input type="radio" name="price" data-testid={`price-${r.id}`}
                checked={filters.priceRange === r.id} onChange={() => setFilters({ ...filters, priceRange: r.id })}
                className="w-4 h-4 accent-pink-500"/>
              {r.label}
            </label>
          ))}
          <button data-testid="price-clear" onClick={() => setFilters({ ...filters, priceRange: null })}
            className="text-xs text-slate-400 hover:text-pink-600 underline">Réinitialiser le prix</button>
        </div>
      </div>

      {/* Sale */}
      <label className="flex items-center justify-between gap-3 rounded-2xl bg-white border border-slate-100 shadow-sm p-4 cursor-pointer">
        <div className="flex items-center gap-2 text-slate-700 text-sm">
          <Tag size={14} className="text-pink-500"/>
          <span className="font-bold">En promo seulement</span>
        </div>
        <input type="checkbox" data-testid="sale-toggle"
          checked={filters.onlySale} onChange={(e) => setFilters({ ...filters, onlySale: e.target.checked })}
          className="w-4 h-4 accent-pink-500"/>
      </label>

      <div className="rounded-2xl p-4 text-center text-xs text-white"
        style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
        <div className="font-black tracking-widest uppercase text-pink-300 text-[10px] mb-1">Résultats</div>
        <div className="font-black text-2xl">{total} produit{total > 1 ? "s" : ""}</div>
      </div>
    </aside>
  );
}

// ============================================================
// CUSTOM JERSEY HIGHLIGHT (featured banner inside grid)
// Real jersey photos (uploaded by user). Used both in the configurator and
// the promo highlight. The "back" photo gets a typography overlay (BEBAS) for
// the player name + number.
const JERSEY_PHOTO_FRONT = "https://customer-assets.emergentagent.com/job_d6b30d32-0560-4729-9096-ffa84a0b3e19/artifacts/m0ick4vn_maillot%20FACE.jpeg";
const JERSEY_PHOTO_BACK  = "https://customer-assets.emergentagent.com/job_d6b30d32-0560-4729-9096-ffa84a0b3e19/artifacts/iis5k99g_maillot%20DOS.jpeg";
const JERSEY_PROMO_VIDEO = "https://customer-assets.emergentagent.com/job_d6b30d32-0560-4729-9096-ffa84a0b3e19/artifacts/qgugo0ac_Video%20promo%20maillot%20perso.mp4";

// Live jersey preview using the real product photos with a BEBAS overlay
// for personalization (name on top, number below — matching the brief).
function JerseyPreview({ side = "back", customName = "", customNumber = "" }) {
  const isBack = side === "back";
  // Wrap image + overlay in a tight inline container so the absolute-positioned
  // text aligns with the actual jersey image bounds (not the parent's whitespace).
  return (
    <div className="relative inline-block max-h-full">
      <img
        src={isBack ? JERSEY_PHOTO_BACK : JERSEY_PHOTO_FRONT}
        alt={isBack ? "Maillot IPHB face dos" : "Maillot IPHB face avant"}
        className="block max-h-[440px] w-auto h-auto object-contain drop-shadow-[0_30px_40px_rgba(0,0,0,0.45)]"
      />
      {isBack && (customName || customNumber) && (
        <div className="absolute inset-0 pointer-events-none">
          {customName && (
            <div
              className="absolute left-0 right-0 font-bebas text-center leading-none"
              style={{
                top: "27%",
                color: "#c7c1ab",
                fontWeight: 800,
                letterSpacing: "0.02em",
                fontSize: "clamp(1.4rem, 4.4vw, 2.6rem)",
              }}>
              {(customName || "").toUpperCase()}
            </div>
          )}
          {customNumber && (
            <div
              className="absolute left-0 right-0 font-bebas text-center leading-[0.85]"
              style={{
                top: "39%",
                color: "#c7c1ab",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                fontSize: "clamp(4.6rem, 14vw, 9rem)",
              }}>
              {customNumber}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
function CustomJerseyHighlight({ onScroll }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      data-testid="custom-jersey-highlight"
      className="col-span-full relative overflow-hidden rounded-3xl shadow-[0_25px_60px_rgba(79,27,124,0.4)]"
      style={{ background: `linear-gradient(125deg, ${PRIMARY} 0%, ${BG_DEEP} 60%, ${BG_DARK} 100%)` }}>
      {/* halftone */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: `radial-gradient(circle, ${SECONDARY} 1px, transparent 1px)`,
        backgroundSize: "16px 16px",
        maskImage: "radial-gradient(ellipse at center, black, transparent 70%)",
      }}/>
      <div className="relative grid sm:grid-cols-[1fr,260px] items-center gap-6 p-6 sm:p-8">
        <div>
          <div className="text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase mb-2 flex items-center gap-2">
            <Sparkles size={12}/> Personnalisation
          </div>
          <h3 className="text-white font-black uppercase italic leading-[0.95]" style={{ fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)" }}>
            Personnalisez votre <span style={{ color: SECONDARY }}>maillot</span>
          </h3>
          <p className="text-white/80 mt-2 max-w-md text-sm">Choisissez votre kit officiel, ajoutez votre nom et votre numéro, et prévisualisez en temps réel face/dos.</p>
          <button onClick={onScroll} data-testid="custom-jersey-cta"
            className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase text-xs tracking-widest shadow-xl hover:scale-105 transition"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
            Personnaliser <ArrowRight size={14}/>
          </button>
        </div>
        <div className="relative w-full aspect-[4/5] max-w-[280px] mx-auto rounded-2xl overflow-hidden ring-2 ring-pink-400/30 shadow-[0_20px_40px_rgba(0,0,0,0.45)]">
          <video
            src={JERSEY_PROMO_VIDEO}
            autoPlay loop muted playsInline
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="jersey-promo-video"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"/>
        </div>
      </div>
    </motion.div>
  );
}

// ============================================================
// PERSONALIZATION CONFIGURATOR (full section, anchor #personnaliser)
// ============================================================
const SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const KITS = [
  { id: "domicile", label: "Maillot Domicile", price: 6500, desc: "Le maillot officiel violet, joué à la Halle Polyvalente.", colors: { main: PRIMARY, accent: SECONDARY } },
];

function ConfiguratorSection() {
  const { add } = useCart();
  const [selectedKit, setSelectedKit] = useState(KITS[0]);
  const [size, setSize] = useState("L");
  const [customName, setCustomName] = useState("");
  const [customNumber, setCustomNumber] = useState("");
  const [side, setSide] = useState("back");
  const [qty, setQty] = useState(1);
  const [persoMode, setPersoMode] = useState("custom"); // "custom" | "player"
  const [roster, setRoster] = useState([]);
  const [selectedPlayerSlug, setSelectedPlayerSlug] = useState("");

  // Charge l'effectif IPHB pour le picker "Joueur de l'équipe"
  useEffect(() => {
    api.get("/sport/team-roster?slug=istres-provence-handball")
      .then((r) => setRoster(r.data?.players || []))
      .catch(() => setRoster([]));
  }, []);

  // Auto-remplit le nom + numéro quand on sélectionne un joueur
  useEffect(() => {
    if (persoMode !== "player" || !selectedPlayerSlug) return;
    const p = roster.find((x) => x.slug === selectedPlayerSlug);
    if (!p) return;
    const lastName = (p.last_name || (p.name || "").split(" ").slice(-1).join("") || "")
      .toUpperCase().slice(0, 14);
    setCustomName(lastName);
    setCustomNumber(String(p.jersey_number ?? "").slice(0, 2));
  }, [selectedPlayerSlug, persoMode, roster]);

  const NAME_FEE = 500;    // 5€ in cents
  const NUMBER_FEE = 1000; // 10€ in cents
  // Le pack joueur officiel = floquage nom + numéro forfaitaire 15 €
  const PLAYER_FEE = 1500;
  const fee = persoMode === "player" && selectedPlayerSlug
    ? PLAYER_FEE
    : (((customName || "").length > 0 ? NAME_FEE : 0) +
       ((customNumber || "").length > 0 ? NUMBER_FEE : 0));
  const personalized = fee > 0;
  const total = (selectedKit.price + fee) * qty;

  const handleAdd = () => {
    add({
      id: `jersey-${selectedKit.id}-${size}-${customName || "X"}-${customNumber || "0"}`,
      slug: `maillot-${selectedKit.id}`,
      name: `${selectedKit.label} · ${size}${personalized ? ` · ${customName || ""} ${customNumber || ""}`.trim() : ""}`,
      price_cents: selectedKit.price + fee,
      featured_image: "",
      stock_tracking: false, stock_qty: 99,
    }, qty);
    toast.success("Maillot ajouté au panier !", {
      description: personalized ? `Personnalisation : ${customName || "—"} #${customNumber || "—"}` : "Sans personnalisation",
    });
  };

  return (
    <section id="personnaliser" data-testid="configurator-section" className="relative py-16 sm:py-20 overflow-hidden" style={{ background: BG_DARK }}>
      <TornEdge position="top" color="#ffffff" variant="rip" height={48}/>
      <div className="absolute inset-0 opacity-15 pointer-events-none" style={{ background: `radial-gradient(ellipse at top right, ${SECONDARY}, transparent 60%)` }}/>
      <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-10">
          <div className="text-pink-400 font-black tracking-[0.4em] text-[11px] uppercase mb-2">Configurateur</div>
          <h2 className="text-white font-black uppercase italic" style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}>
            Personnalisez votre <span style={{ color: SECONDARY }}>maillot</span>
          </h2>
          <p className="text-white/70 mt-3 max-w-2xl mx-auto text-sm">Choisissez votre kit, ajoutez votre nom et votre numéro, prévisualisez en direct.</p>
        </div>

        <div className="grid lg:grid-cols-[1.1fr,1fr] gap-10 items-start">
          {/* PREVIEW */}
          <div className="relative">
            <div className="sticky top-24">
              <div className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_rgba(79,27,124,0.5)]"
                style={{ background: `linear-gradient(135deg, ${BG_DEEP}, #2c1538)` }}>
                <div className="absolute top-4 right-4 z-10 flex gap-1 bg-black/40 backdrop-blur rounded-full p-1">
                  <button data-testid="jersey-side-front" onClick={() => setSide("front")}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition ${side === "front" ? "bg-pink-500 text-white" : "text-white/70 hover:text-white"}`}>Face</button>
                  <button data-testid="jersey-side-back" onClick={() => setSide("back")}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition ${side === "back" ? "bg-pink-500 text-white" : "text-white/70 hover:text-white"}`}>Dos</button>
                </div>
                <motion.div key={`${side}-${selectedKit.id}`}
                  initial={{ rotateY: -90, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }}
                  transition={{ duration: 0.5, ease: [0.21, 0.94, 0.4, 1] }}
                  className="relative aspect-[4/5] flex items-center justify-center p-6">
                  <div className="absolute inset-12 rounded-full" style={{ background: `radial-gradient(circle, ${selectedKit.colors.accent}33 0%, transparent 70%)`, filter: "blur(40px)" }}/>
                  <div className="relative w-full h-full max-w-[460px]">
                    <JerseyPreview side={side} customName={customName} customNumber={customNumber}/>
                  </div>
                </motion.div>
                <p className="px-6 pb-3 text-center text-[10px] sm:text-[11px] italic text-white/45 leading-snug"
                   data-testid="jersey-preview-disclaimer">
                  Image non contractuelle — ceci n'est qu'un aperçu. Les dimensions des marquages sont standardisées.
                </p>
                <div className="px-6 py-4 flex items-center justify-between border-t border-white/10 bg-black/30">
                  <div>
                    <div className="text-pink-400 text-[10px] font-black uppercase tracking-widest">Aperçu en direct</div>
                    <div className="text-white text-sm font-bold mt-0.5">{selectedKit.label} — {side === "front" ? "Face" : "Dos"}</div>
                  </div>
                  <button onClick={() => setSide(side === "front" ? "back" : "front")}
                    className="w-11 h-11 rounded-full bg-pink-500 text-white flex items-center justify-center hover:scale-105 transition shadow-lg">
                    <RotateCw size={16}/>
                  </button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                {[
                  { icon: Truck, label: "Livraison 7-10j" },
                  { icon: Shield, label: "Floquage officiel" },
                  { icon: Heart, label: "Soutien direct au club" },
                ].map((it, i) => (
                  <div key={i} className="rounded-2xl p-3 bg-white/5 border border-white/10">
                    <it.icon size={18} className="text-pink-400 mx-auto mb-1"/>
                    <div className="text-white/70 text-[10px] font-bold uppercase tracking-wider leading-tight">{it.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CONFIGURATOR */}
          <div className="space-y-6">
            <div>
              <div className="text-pink-400 font-black tracking-[0.3em] text-[11px] uppercase mb-3 flex items-center gap-2"><Sparkles size={12}/> 1. Choisissez votre kit</div>
              <div className="grid sm:grid-cols-3 gap-3">
                {KITS.map((k) => (
                  <button data-testid={`kit-${k.id}`} key={k.id} onClick={() => setSelectedKit(k)}
                    className={`relative rounded-2xl p-4 text-left transition border-2 ${selectedKit.id === k.id ? "border-pink-500 shadow-[0_0_24px_rgba(236,31,124,0.4)]" : "border-white/10 hover:border-white/30"}`}
                    style={{ background: `linear-gradient(135deg, ${k.colors.main}, ${BG_DEEP})` }}>
                    {selectedKit.id === k.id && <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-pink-500 text-white flex items-center justify-center"><Check size={14}/></div>}
                    <div className="text-white font-black uppercase text-sm leading-tight">{k.label}</div>
                    <div className="text-pink-300 font-black text-lg mt-1">{priceFmt(k.price)}</div>
                    <div className="text-white/60 text-[11px] mt-2 leading-snug">{k.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-pink-400 font-black tracking-[0.3em] text-[11px] uppercase mb-3 flex items-center gap-2"><Sparkles size={12}/> 2. Taille</div>
              <div className="flex flex-wrap gap-2">
                {SIZES.map((s) => (
                  <button data-testid={`size-${s}`} key={s} onClick={() => setSize(s)}
                    className={`min-w-[52px] px-4 py-2.5 rounded-full font-black uppercase tracking-widest text-sm transition ${size === s ? "bg-pink-500 text-white shadow-lg" : "bg-white/5 text-white border border-white/15 hover:bg-white/10"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl p-5 border-2 border-pink-500/30" style={{ background: `linear-gradient(135deg, rgba(236,31,124,0.08), rgba(79,27,124,0.15))` }}>
              <div className="text-pink-400 font-black tracking-[0.3em] text-[11px] uppercase mb-3 flex items-center gap-2">
                <Sparkles size={12}/> 3. Personnalisation
              </div>

              {/* Mode selector */}
              <div className="mb-4 inline-flex p-1 rounded-full bg-black/40 border border-white/10">
                {[
                  { id: "player", label: "Joueur de l'équipe" },
                  { id: "custom", label: "Mon prénom & numéro" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setPersoMode(opt.id);
                      setSelectedPlayerSlug("");
                      setCustomName("");
                      setCustomNumber("");
                    }}
                    data-testid={`perso-mode-${opt.id}`}
                    className={`px-3 sm:px-4 py-2 rounded-full font-black uppercase text-[10px] tracking-widest transition ${persoMode === opt.id ? "bg-pink-500 text-white shadow-[0_0_18px_rgba(236,31,124,0.5)]" : "text-white/50 hover:text-white"}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {persoMode === "player" ? (
                <div>
                  <label className="text-white/60 text-[10px] uppercase tracking-widest font-bold flex items-center gap-2">
                    Sélectionnez un joueur
                    <span className="bg-pink-500/30 text-pink-100 px-2 py-0.5 rounded-full text-[9px] tracking-normal">+15 €</span>
                  </label>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto pr-1"
                       data-testid="player-picker">
                    {roster.length === 0 && (
                      <div className="col-span-full text-white/40 text-xs italic py-4 text-center">Chargement de l'effectif…</div>
                    )}
                    {roster.map((p) => {
                      const lastName = (p.last_name || (p.name || "").split(" ").slice(-1).join("") || "").toUpperCase();
                      return (
                        <button
                          type="button"
                          key={p.slug}
                          onClick={() => setSelectedPlayerSlug(p.slug)}
                          data-testid={`player-pick-${p.slug}`}
                          className={`relative rounded-xl px-2.5 py-2 text-left transition border ${selectedPlayerSlug === p.slug ? "border-pink-500 bg-pink-500/15 shadow-[0_0_18px_rgba(236,31,124,0.35)]" : "border-white/10 bg-black/30 hover:border-white/30"}`}
                        >
                          <div className="flex items-center gap-2">
                            <div className="shrink-0 w-9 h-9 rounded-full bg-white/10 ring-1 ring-white/15 flex items-center justify-center font-bebas font-black text-pink-300 text-lg leading-none">
                              {p.jersey_number ?? "—"}
                            </div>
                            <div className="min-w-0">
                              <div className="text-white font-black uppercase text-[11px] truncate leading-tight">{lastName}</div>
                              <div className="text-white/50 text-[9px] uppercase tracking-wider truncate">{p.position || ""}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {selectedPlayerSlug && (
                    <div className="mt-3 text-[11px] text-pink-200/80 flex items-center justify-between gap-2 flex-wrap">
                      <span>Floquage officiel : <span className="font-black text-white">{customName} #{customNumber}</span></span>
                      <button onClick={() => { setSelectedPlayerSlug(""); setCustomName(""); setCustomNumber(""); }}
                        className="text-pink-300 hover:text-white underline text-[10px]">Réinitialiser</button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="grid sm:grid-cols-[1fr,110px] gap-3">
                    <div>
                      <label className="text-white/60 text-[10px] uppercase tracking-widest font-bold flex items-center gap-2">
                        Prénom / Nom
                        <span className="bg-pink-500/30 text-pink-100 px-2 py-0.5 rounded-full text-[9px] tracking-normal">+5 €</span>
                      </label>
                      <input data-testid="custom-name-input" value={customName}
                        onChange={(e) => setCustomName(e.target.value.replace(/[^A-Za-zÀ-ÿ\s'-]/g, "").slice(0, 14))}
                        maxLength={14} placeholder="VOTRE PRÉNOM"
                        className="mt-1 w-full px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white font-black uppercase tracking-widest focus:border-pink-500 focus:outline-none placeholder:text-white/25"/>
                      <div className="text-[10px] text-white/40 mt-1">Max 14 caractères</div>
                    </div>
                    <div>
                      <label className="text-white/60 text-[10px] uppercase tracking-widest font-bold flex items-center gap-2">
                        Numéro
                        <span className="bg-pink-500/30 text-pink-100 px-2 py-0.5 rounded-full text-[9px] tracking-normal">+10 €</span>
                      </label>
                      <input data-testid="custom-number-input" type="text" value={customNumber}
                        onChange={(e) => setCustomNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
                        maxLength={2} placeholder="10"
                        className="mt-1 w-full px-4 py-3 rounded-xl bg-black/40 border border-white/15 text-white font-black uppercase text-center text-2xl focus:border-pink-500 focus:outline-none placeholder:text-white/25"/>
                      <div className="text-[10px] text-white/40 mt-1">0-99</div>
                    </div>
                  </div>
                  {fee > 0 && (
                    <div data-testid="personalization-fee-summary"
                      className="mt-3 text-[11px] text-pink-200/80 flex items-center justify-between">
                      <span>Floquage : {customName ? "Prénom" : ""}{customName && customNumber ? " + " : ""}{customNumber ? "Numéro" : ""}</span>
                      <span className="font-black text-pink-300">+{priceFmt(fee)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-2xl p-5 bg-white/5 border border-white/10">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-white/70 text-xs uppercase tracking-widest font-bold">Qté</span>
                  <div className="flex items-center bg-black/40 rounded-full">
                    <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 text-white hover:text-pink-400 font-black">−</button>
                    <span data-testid="qty-value" className="w-8 text-center text-white font-black">{qty}</span>
                    <button data-testid="qty-plus" onClick={() => setQty(Math.min(10, qty + 1))} className="w-9 h-9 text-white hover:text-pink-400 font-black">+</button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white/50 text-[10px] uppercase tracking-widest">Total</div>
                  <div data-testid="cart-total" className="text-pink-400 font-black text-3xl">{priceFmt(total)}</div>
                </div>
              </div>
              <button data-testid="add-to-cart-btn" onClick={handleAdd}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-black uppercase text-sm tracking-widest shadow-2xl hover:scale-[1.02] transition"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
                <ShoppingBag size={18}/> Ajouter au panier <ArrowRight size={16}/>
              </button>
              <div className="text-center text-white/40 text-[11px] mt-3">Paiement sécurisé · Production sur commande · 7-10 jours ouvrés</div>
            </div>
          </div>
        </div>
      </div>
      <TornEdge position="bottom" color="#ffffff" variant="shred" height={48}/>
    </section>
  );
}

// ============================================================
// QUICK SIZE PICKER — modal compact pour choisir la taille
// ============================================================
function QuickSizePicker({ product, onClose, onConfirm }) {
  const [size, setSize] = useState("");
  const sizes = getProductSizes(product);
  if (!product) return null;
  const handleConfirm = () => {
    if (!size) {
      toast.error("Choisissez une taille");
      return;
    }
    onConfirm(product, size);
    onClose();
  };
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
        data-testid="quick-size-modal"
      >
        <motion.div
          initial={{ y: 40, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full sm:max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
        >
          <button onClick={onClose} aria-label="Fermer"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition z-10"
            data-testid="quick-size-close">
            <X size={16}/>
          </button>
          <div className="flex gap-3 p-4 pb-2 items-center">
            <div className="w-20 h-20 bg-slate-50 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
              <img src={product.image} alt={product.name} className="max-w-full max-h-full object-contain"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-black">{product.brand}</div>
              <div className="font-black text-sm text-slate-900 line-clamp-2 leading-tight">{product.name}</div>
              <div className="font-black text-base mt-0.5" style={{ color: SECONDARY, fontFamily: "'Bebas Neue', sans-serif" }}>{priceFmt(product.price)}</div>
            </div>
          </div>
          <div className="px-4 pb-4">
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-700 font-black mb-2.5">Choisissez votre taille</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {(sizes || []).map((s) => {
                const active = size === s;
                return (
                  <button key={s} type="button" onClick={() => setSize(s)}
                    data-testid={`quick-size-${s}`}
                    className={`min-w-[48px] px-3 h-11 rounded-lg font-black text-xs uppercase tracking-wider border-2 transition ${active ? "text-white shadow-md" : "bg-white text-slate-700 border-slate-200 hover:border-pink-400 hover:text-pink-600"}`}
                    style={active ? { background: SECONDARY, borderColor: SECONDARY } : undefined}>
                    {s}
                  </button>
                );
              })}
            </div>
            <button onClick={handleConfirm} disabled={!size}
              data-testid="quick-size-confirm"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-black uppercase text-sm tracking-widest text-white shadow-xl hover:scale-[1.02] active:scale-[0.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
              <ShoppingBag size={16}/> Ajouter au panier
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ============================================================
// MAIN BOUTIQUE PAGE
// ============================================================
export default function SportBoutique() {
  const { add } = useCart();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ q: "", cat: "all", brands: [], priceRange: null, onlySale: false });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [pendingSizeProduct, setPendingSizeProduct] = useState(null);

  // Read ?cat=... from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cat = params.get("cat");
    if (cat) setFilters((f) => ({ ...f, cat }));
  }, []);

  // Fetch products from back-office (sport_products collection)
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/sport/products").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.products || []);
      // Fallback to bundled products if the DB is empty (first deploy before seed runs).
      setProducts(list.length ? list : STORE_PRODUCTS);
    }).catch(() => setProducts(STORE_PRODUCTS));
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (filters.cat !== "all") {
        // a category id matches either group or category
        if (p.group !== filters.cat && p.category !== filters.cat) return false;
      }
      if (filters.brands.length && !filters.brands.includes(p.brand)) return false;
      if (filters.priceRange) {
        const r = STORE_PRICE_RANGES.find((x) => x.id === filters.priceRange);
        if (r && (p.price < r.min || p.price > r.max)) return false;
      }
      if (filters.onlySale && !p.sale) return false;
      if (filters.q && !p.name.toLowerCase().includes(filters.q.toLowerCase())) return false;
      return true;
    });
  }, [filters, products]);

  const handleAdd = (p, quantity = 1) => {
    // Si le produit a des tailles, ouvre le picker plutôt que d'ajouter direct
    const sizes = getProductSizes(p);
    if (sizes && sizes.length > 0) {
      setPendingSizeProduct(p);
      return;
    }
    add({
      id: p.id,
      slug: p.id,
      name: p.name,
      price_cents: p.price,
      featured_image: p.image,
      stock_tracking: false, stock_qty: 99,
    }, quantity);
    toast.success("Ajouté au panier", { description: p.name });
  };

  const handleConfirmSize = (p, size) => {
    add(
      {
        id: p.id,
        slug: p.id,
        name: p.name,
        price_cents: p.price,
        featured_image: p.image,
        stock_tracking: false, stock_qty: 99,
      },
      1,
      { id: size, options: { Taille: size } },
    );
    toast.success("Ajouté au panier", { description: `${p.name} · ${size}` });
  };

  const scrollToConfig = () => {
    document.getElementById("personnaliser")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div style={{ background: "#fbf7fa" }}>
      {/* HERO BANNER CAROUSEL */}
      <HeroCarousel banners={HERO_BANNERS}/>

      {/* PRODUCT GRID + SIDEBAR — wrapped with sponsor backdrop */}
      <section className="relative py-12 sm:py-16 overflow-hidden">
        {/* Sponsor logos animated backdrop */}
        <SponsorBackdrop/>

        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          {/* Title */}
          <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
            <div>
              <div className="text-pink-600 font-black tracking-[0.3em] text-xs uppercase mb-2">Boutique officielle 25/26</div>
              <h1 className="font-black uppercase italic" style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", color: PRIMARY }}>
                La <span style={{ color: SECONDARY }}>collection</span> du club
              </h1>
            </div>
            <button data-testid="mobile-filter-btn"
              onClick={() => setMobileFiltersOpen(true)}
              className="lg:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg"
              style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
              <Filter size={14}/> Filtres
            </button>
          </div>

          <div className="grid lg:grid-cols-[280px,1fr] gap-8">
            {/* desktop sidebar */}
            <div className="hidden lg:block">
              <FilterSidebar filters={filters} setFilters={setFilters} total={filtered.length}/>
            </div>

            <div className="min-w-0">
              {/* Mobile inline category chips — always visible quick filter */}
              <div className="lg:hidden mb-5 -mx-4 px-4 overflow-x-auto pb-2 max-w-full" data-testid="mobile-cat-chips" style={{ WebkitOverflowScrolling: "touch" }}>
                <div className="flex gap-2 w-max">
                  {STORE_CATEGORIES.map((c) => {
                    const active = filters.cat === c.id;
                    return (
                      <button
                        key={c.id}
                        data-testid={`chip-cat-${c.id}`}
                        onClick={() => setFilters({ ...filters, cat: c.id })}
                        className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-black uppercase tracking-widest border transition ${
                          active
                            ? "bg-pink-500 text-white border-pink-500 shadow-md"
                            : "bg-white text-slate-700 border-slate-200 hover:border-pink-400"
                        }`}
                      >
                        {c.label}
                        <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>{c.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* main grid */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
                {/* Featured custom jersey banner — always at top */}
                <CustomJerseyHighlight onScroll={scrollToConfig}/>

                {/* products */}
                {filtered.length === 0 ? (
                  <div className="col-span-full text-center text-slate-500 italic py-16 bg-white rounded-2xl border border-slate-100">
                    Aucun produit ne correspond à votre sélection.
                  </div>
                ) : (
                  filtered.map((p) => <ProductCard key={p.id} p={p} onAdd={handleAdd} onOpen={(prod) => navigate(`/sport/boutique/produit/${prod.id}`)}/>)
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONFIGURATOR */}
      <ConfiguratorSection/>

      {/* MOBILE FILTER DRAWER */}
      <AnimatePresence>
        {mobileFiltersOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setMobileFiltersOpen(false)}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm lg:hidden">
            <motion.div onClick={(e) => e.stopPropagation()}
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 26 }}
              className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-[#fbf7fa] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-black text-xl uppercase italic" style={{ color: PRIMARY }}>Filtres</h3>
                <button onClick={() => setMobileFiltersOpen(false)} className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center"><X size={18}/></button>
              </div>
              <FilterSidebar filters={filters} setFilters={setFilters} total={filtered.length}/>
              <button onClick={() => setMobileFiltersOpen(false)}
                className="mt-6 w-full px-5 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                Voir les {filtered.length} produits
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* QUICK SIZE PICKER MODAL */}
      {pendingSizeProduct && (
        <QuickSizePicker
          product={pendingSizeProduct}
          onClose={() => setPendingSizeProduct(null)}
          onConfirm={handleConfirmSize}
        />
      )}
    </div>
  );
}
